import { createContext, Dispatch, SetStateAction, useContext } from 'react';

import noop, { NoOp } from 'utils/noop';

import { defaultImgMetadata } from '../constants';
import {
  Asset,
  ImageAttrAction,
  ImageAttrState,
  ImageMetadata,
  PanelIntent,
  PanelView,
  UploadCompleteConfig,
} from '../types';
import { imageAttrStateDefault } from '../utils/images';

export interface PanelContextState {
  assetUrl: string;
  hideGifs: boolean;
  imageAttrState: ImageAttrState; // located in the context so that it can persist between view switching (List <-> Details)
  imgMetadata: ImageMetadata;
  nonCustomizableAttrs: Record<string, string>;
  onCancel: NoOp;
  onClose: NoOp;
  onInsert?: (attrs: Record<string, string>) => void; // returns an object of element attributes
  onNewFiles: (fileCount?: number) => void;
  onSelect?: (url: string, imgMetadata?: ImageMetadata) => void; // returns url for text field (and optionally the image metadata)
  onUploadComplete: (config: UploadCompleteConfig) => void;
  panelIntent: PanelIntent;
  initialIntent: PanelIntent;
  setPanelIntent: (pi: PanelIntent) => void;
  panelSearch: string;
  panelView: PanelView;
  providerHasInitiated: boolean;
  scrollBookmark: number;
  selectedAsset?: Asset; // a selected asset from the asset manager
  selectedEditorImageNode?: HTMLImageElement; // a currently selected image node in the WYSIWYG editor (could be previous Asset Manager asset or remote image node)
  selectedRemoteImageNode?: HTMLImageElement; // a new remote image entered as a url in the details view
  setImageAttrState: Dispatch<ImageAttrAction>;
  setNonCustomizableAttrs: Dispatch<SetStateAction<Record<string, string>>>;
  setPanelSearch: (newPanelSearch: string) => void;
  setPanelView: (pv: PanelView) => void;
  setScrollBookmark: (pos: number) => void;
  setSelectedAsset: (asset: Asset) => void;
  setSelectedRemoteImageNode: (elt: HTMLImageElement) => void;
}

export const panelContextDefaults: PanelContextState = {
  assetUrl: '',
  hideGifs: false,
  imageAttrState: imageAttrStateDefault,
  imgMetadata: defaultImgMetadata,
  nonCustomizableAttrs: {},
  onCancel: noop,
  onClose: noop,
  onInsert: undefined,
  onNewFiles: noop,
  onSelect: undefined,
  onUploadComplete: noop,
  panelIntent: PanelIntent.Browse,
  initialIntent: PanelIntent.Browse,
  setPanelIntent: noop,
  panelSearch: '',
  panelView: PanelView.List,
  providerHasInitiated: false,
  scrollBookmark: 0,
  selectedAsset: undefined,
  selectedEditorImageNode: undefined,
  selectedRemoteImageNode: undefined,
  setImageAttrState: noop,
  setNonCustomizableAttrs: noop,
  setPanelSearch: noop,
  setPanelView: noop,
  setScrollBookmark: noop,
  setSelectedAsset: noop,
  setSelectedRemoteImageNode: noop,
};

export const PanelContext = createContext<PanelContextState>(panelContextDefaults);

export const usePanelContext = () => useContext(PanelContext);
